/**
 * The sort control in the review grid's column headers: a chevron at the right
 * of every sortable header, its own click target. The header label keeps
 * Handsontable's select-on-click, so selecting a column and sorting by it are
 * two different clicks in the same header — the spreadsheet convention
 * (Google Sheets' header chevron, Excel's filter button).
 *
 * Handsontable renders headers outside React, so the control is drawn
 * imperatively from `afterGetColHeader`, and its click is caught by a
 * delegated handler on the grid's wrapper. Utility classes are literal
 * strings here so Tailwind picks them up.
 */

/** How a sortable column reads in its header. */
export type SortToggleState = 'asc' | 'desc' | 'none';

const SORT_TOGGLE_CLASS = 'as400-sort-toggle';
const SORT_COLUMN_ATTR = 'data-sort-col';

// Hidden until the header is hovered (see SORT_TOGGLE_HOST_CLASSES), then in
// full contrast; the sorted column's shows outright, with the direction, in
// the primary colour. A pointer that can't hover (touch) sees every chevron.
// Inline, so the header's measured width (Handsontable's ghost table runs the
// header hook too) keeps room for it and the label is never covered.
const SORT_TOGGLE_CLASSES = `${SORT_TOGGLE_CLASS} ml-1 inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded align-middle text-[13px] leading-none text-foreground opacity-0 hover:bg-accent data-[sorted]:text-primary data-[sorted]:opacity-100 [@media(hover:none)]:opacity-100`;

/** For the grid's wrapper: reveal a header's chevron while it is hovered. */
export const SORT_TOGGLE_HOST_CLASSES =
  '[&_th:hover_.as400-sort-toggle]:opacity-100';

const GLYPH: Record<SortToggleState, string> = {
  asc: '▲',
  desc: '▼',
  none: '⇅',
};
const ARIA_SORT: Record<SortToggleState, string> = {
  asc: 'ascending',
  desc: 'descending',
  none: 'none',
};

/**
 * Draw (or update, or remove) the header's sort control. Handsontable recycles
 * header cells across redraws, so a cell is always brought to exactly the
 * state its current column calls for; `state` undefined means the column
 * can't be sorted (a spacer) and gets no control.
 */
export function renderSortToggle(
  th: HTMLTableCellElement,
  colIndex: number,
  state: SortToggleState | undefined,
  label: string,
): void {
  // Handsontable wraps header content in `div.relative`; the control is a
  // sibling of the label span, so re-rendering the label leaves it in place.
  const host = th.firstElementChild ?? th;
  let toggle = host.querySelector<HTMLButtonElement>(`.${SORT_TOGGLE_CLASS}`);
  if (state === undefined) {
    toggle?.remove();
    th.removeAttribute('aria-sort');
    return;
  }
  if (!toggle) {
    toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = SORT_TOGGLE_CLASSES;
    host.appendChild(toggle);
  }
  toggle.setAttribute(SORT_COLUMN_ATTR, String(colIndex));
  toggle.setAttribute('aria-label', label);
  toggle.textContent = GLYPH[state];
  if (state === 'none') toggle.removeAttribute('data-sorted');
  else toggle.setAttribute('data-sorted', state);
  th.setAttribute('aria-sort', ARIA_SORT[state]);
}

/** The sort control under `target`, if the event landed on one. */
export function sortToggleFrom(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLElement>(`.${SORT_TOGGLE_CLASS}`);
}

/** The visual column a sort control belongs to. */
export function sortToggleColumn(toggle: HTMLElement): number {
  return Number(toggle.getAttribute(SORT_COLUMN_ATTR));
}
